// Forward-year income forecast (e.g. 2027), returned in the SAME shape as
// buildPLIncome so the P&L view consumes it identically.
//
// Rules (REVENUE_ENGINE_SPEC.md section 11 "multi-year"):
//   - Recurring (subscription / bookkeeping / CAO) and AUM engagements CONTINUE
//     and annualize: a client active on Jan 1 of the forecast year bills a full
//     12 months; the engine's activeMonthIndices handles mid-year starts and
//     prior-year churns (a client churned in the prior year contributes 0).
//   - Tax prep and commissions MIRROR the prior year's full-year total. They
//     recur annually in reality but don't auto-renew in the client record, so
//     re-projecting per-record would miss them; instead we carry the prior-year
//     total forward, spread evenly.
//   - Tax planning STARTS EMPTY: it's a one-time engagement with no renewal, so
//     the forecast year has $0 until new tax-plan deals are signed.

import { mapClientToEngagements } from './mapClientToEngagements.mjs';
import { engagementContribution, MONTH_KEYS } from './engine.mjs';
import { buildPLIncome } from './plIncome.mjs';

const MK = MONTH_KEYS;
const CAT_DIVISION = {
  subscription_fees: 'bwm', investment_management: 'bwm', tax_planning: 'bwm',
  tax_preparation: 'bts', bookkeeping: 'bts', cao: 'bts', commissions: 'bia',
};
// Engine engagement.category -> P&L income-category key, for the categories
// that CONTINUE into the forecast year via the engine projection.
const CONTINUING_TO_PL = {
  subscription: 'subscription_fees',
  investment_management: 'investment_management',
  bookkeeping: 'bookkeeping',
  cao: 'cao',
};

function fullYearTotal(monthsObj, cat) {
  let t = 0;
  for (const mk of MK) t += Number(monthsObj[mk]?.income?.[cat]) || 0;
  return t;
}

// { global, byClass, priorYear, priorTaxPrep, priorCommissions }
export function buildForecastIncome(clients, classActuals, forecastYear) {
  const priorYear = forecastYear - 1;
  // Prior-year reconciled income (actuals + committed) supplies the tax-prep and
  // commissions totals to mirror.
  const prior = buildPLIncome(clients || [], classActuals, priorYear);
  const priorTaxPrep = fullYearTotal(prior.global.months, 'tax_preparation');
  const priorCommissions = fullYearTotal(prior.global.months, 'commissions');

  const byCategory = {
    subscription_fees: new Array(12).fill(0),
    investment_management: new Array(12).fill(0),
    bookkeeping: new Array(12).fill(0),
    cao: new Array(12).fill(0),
    tax_preparation: new Array(12).fill(0),
    tax_planning: new Array(12).fill(0), // one-time, no renewal -> stays empty
    commissions: new Array(12).fill(0),
  };

  // Continuing recurring / AUM engagements, projected for the forecast year.
  const engagements = (clients || []).flatMap(mapClientToEngagements);
  for (const eng of engagements) {
    const plCat = CONTINUING_TO_PL[eng.category];
    if (!plCat) continue; // tax prep / tax planning / commissions handled below
    const { months } = engagementContribution(eng, forecastYear);
    for (let m = 0; m < 12; m++) byCategory[plCat][m] += months[m];
  }

  // Mirror prior-year tax prep + commissions, spread evenly across the year.
  const taxPrepPer = priorTaxPrep / 12;
  const commissionsPer = priorCommissions / 12;
  for (let m = 0; m < 12; m++) {
    byCategory.tax_preparation[m] = taxPrepPer;
    byCategory.commissions[m] = commissionsPer;
  }

  // Assemble global + per-division, matching buildPLIncome's output shape.
  const global = { months: {} };
  const byClass = { bwm: { months: {} }, bts: { months: {} }, bia: { months: {} } };
  for (let m = 0; m < 12; m++) {
    const g = {};
    const perClass = { bwm: {}, bts: {}, bia: {} };
    for (const cat of Object.keys(byCategory)) {
      const v = Math.round((byCategory[cat][m] || 0) * 100) / 100;
      g[cat] = v;
      perClass[CAT_DIVISION[cat] || 'bts'][cat] = v;
    }
    global.months[MK[m]] = { income: g };
    for (const cls of ['bwm', 'bts', 'bia']) byClass[cls].months[MK[m]] = { income: perClass[cls] };
  }
  return { global, byClass, priorYear, priorTaxPrep, priorCommissions };
}
